import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import { alignLeftCellStyle, alignRightCellStyle } from '@client-shared/ui-agGrid';

import { verifyEditableCondition, verifyHiddenCondition } from './utils';
import {
  getBaseCompareVarianceTotalColumn,
  getTotalColumn,
} from './totalColumn';

import { YES_FLAG } from '../../shared/constants';
import { ROW_TYPES, LABEL_COLUMN_ID, BASE_PREFIX, VARIANCE_PREFIX, COMPARE_PREFIX } from './constants';
import { ReportCellRendererFactory } from './columnRenderers';

const ORDER_BY_COLUMNS = ['COMPANY_CODE', 'COUNTRY_TAX_TYPE'];

const isHidden = ({ fieldDefinition, report }) => {
  if (!fieldDefinition.hiddenConditions) {
    return false;
  }

  if (Array.isArray(fieldDefinition.hiddenConditions)) {
    return verifyHiddenCondition(fieldDefinition.hiddenConditions, report);
  }

  if (Array.isArray(fieldDefinition.hiddenConditions[report.reportName])) {
    return verifyHiddenCondition(fieldDefinition.hiddenConditions[report.reportName], report);
  }

  return false;
};

const valueGetterFactory = ({ report, prefix }) => ({ data: fieldDefinition }) => {
  const from = typeof fieldDefinition.from === 'object'
    ? fieldDefinition.from[report.reportName]
    : fieldDefinition.from;
  const { displayNullAsZero } = fieldDefinition;

  if (!from) {
    return null;
  }

  if (isHidden({ fieldDefinition, report })) {
    return null;
  }

  const value = report[`${prefix}${from}`];

  if (value === null && displayNullAsZero) {
    return 0;
  }

  return value;
};

const isSummaryCell = ({ data: fieldDefinition }) => fieldDefinition.type === ROW_TYPES.SUMMARY;

const getColumnDefinition = ({
  headerName,
  headerClass = '',
  cellClass = '',
  prefix = '',
  report,
  setValue,
  isBaseScenario,
  baseScenarioSettings,
  compareScenarioSettings,
  disableEditing,
  isIncludedInTotalColumn = false,
}) => {
  const scenarioSettings = isBaseScenario ? baseScenarioSettings : compareScenarioSettings;
  const {
    reportName,
    countryTaxType,
    companyCode,
  } = report;

  const isEditable = ({ data: fieldDefinition }) => {
    if (!scenarioSettings || scenarioSettings.lockedFlag === YES_FLAG || disableEditing) {
      return false;
    }
    if (typeof fieldDefinition.editable === 'object') {
      if (fieldDefinition.editable[reportName] && fieldDefinition.editable[reportName].conditions) {
        return verifyEditableCondition(
          fieldDefinition.editable[reportName].conditions,
          report
        );
      }

      return fieldDefinition.editable[reportName];
    }

    return fieldDefinition.editable;
  };

  const customColDefFields = {
    isBaseScenario,
    scenarioSettings,
    baseScenarioSettings,
    compareScenarioSettings,
    reportName,
    countryTaxType,
    companyCode,
    isIncludedInTotalColumn,
    isVarianceColumn: false,
  };

  return {
    headerName,
    field: `${prefix}${reportName}${companyCode}${countryTaxType}`,
    colId: `${prefix}${reportName}${companyCode}${countryTaxType}`,
    headerClass,
    cellClass,
    cellRenderer: ReportCellRendererFactory({ report, prefix }),
    valueGetter: valueGetterFactory({ report, prefix }),
    valueSetter: ({ data: fieldDefinition, newValue }) => {
      const fieldName = typeof fieldDefinition.from === 'object'
        ? fieldDefinition.from[reportName]
        : fieldDefinition.from;

      setValue({
        reportName,
        fieldName,
        companyCode,
        countryTaxType,
        value: parseFloat(newValue),
        compareScenario: prefix === COMPARE_PREFIX,
      });
    },
    editable: isEditable,
    cellClassRules: {
      'disable-editing': params => !isEditable(params),
      'cell-summary': isSummaryCell,
    },
    minWidth: 100,
    width: 120,
    ...alignRightCellStyle,
    ...customColDefFields,
  };
};

const getBaseCompareVarianceColumns = ({
  report,
  setValue,
  scenarioSettingsCollection,
  headerClass,
  cellClass,
  disableEditingBase,
  disableEditingCompare,
}) => [
  getColumnDefinition({
    headerName: '1st scenario',
    prefix: BASE_PREFIX,
    headerClass,
    cellClass,
    report,
    setValue,
    isBaseScenario: true,
    baseScenarioSettings: scenarioSettingsCollection.base,
    compareScenarioSettings: scenarioSettingsCollection.compare,
    disableEditing: disableEditingBase,
    isIncludedInTotalColumn: true,
  }),
  getColumnDefinition({
    headerName: '2nd scenario',
    prefix: COMPARE_PREFIX,
    report,
    setValue,
    isBaseScenario: false,
    baseScenarioSettings: scenarioSettingsCollection.base,
    compareScenarioSettings: scenarioSettingsCollection.compare,
    disableEditing: disableEditingCompare,
    isIncludedInTotalColumn: true,
  }),
  {
    headerName: 'Variance',
    colId: `${VARIANCE_PREFIX}${report.reportName}${report.companyCode}${report.countryTaxType}`,
    cellRenderer: ReportCellRendererFactory({
      report,
      prefix: VARIANCE_PREFIX,
    }),
    valueGetter: valueGetterFactory({
      report,
      prefix: VARIANCE_PREFIX,
    }),
    cellClassRules: {
      'cell-variance': () => true,
    },
    minWidth: 100,
    width: 120,
    ...alignLeftCellStyle,
    isVarianceColumn: true,
    companyCode: report.companyCode,
    countryTaxType: report.countryTaxType,
  },
];

const getColumnsPerGroup = ({
  reportData,
  setValue,
  isComparingMode,
  headerClass,
  cellClass,
  scenarioSettingsCollection,
  scenarioMembersMap,
  compareScenarioMembersMap,
  isFunctionalCurrencySelected,
}) => {
  const report = {
    countryTaxType: reportData.COUNTRY_TAX_TYPE,
    companyCode: reportData.COMPANY_CODE,
    ...reportData,
  };

  const isScenarioMemberValid = Boolean(scenarioMembersMap[report.companyCode]);
  const isCompareScenarioMemberValid = Boolean(compareScenarioMembersMap[report.companyCode]);

  if (!isComparingMode) {
    return getColumnDefinition({
      headerName: report.countryTaxType,
      report,
      setValue,
      headerClass,
      cellClass,
      isBaseScenario: true,
      baseScenarioSettings: scenarioSettingsCollection.base,
      disableEditing: !isScenarioMemberValid || !isFunctionalCurrencySelected,
      isIncludedInTotalColumn: true,
    });
  }

  return {
    headerName: report.countryTaxType,
    colId: `${report.companyCode}${report.countryTaxType}`,
    headerClass,
    children: getBaseCompareVarianceColumns({
      report,
      setValue,
      headerClass,
      cellClass,
      scenarioSettingsCollection,
      disableEditingBase: !isScenarioMemberValid || !isFunctionalCurrencySelected,
      disableEditingCompare: !isCompareScenarioMemberValid || !isFunctionalCurrencySelected,
    }),
  };
};

const flattenReportsData = reportsData => Object.entries(reportsData)
  .flatMap(([reportName, reportData]) => reportData.map(rd => ({
    ...rd,
    reportName,
  })));

const getColumnDefinitionsGroupedBy = ({
  reportsData,
  setValue,
  isComparingMode,
  scenarioSettingsCollection,
  scenarioMembersMap,
  compareScenarioMembersMap,
  groupByKey,
  isFunctionalCurrencySelected,
}) => {
  const flatReportsData = flattenReportsData(reportsData);
  const sortedFlatReportsData = orderBy(flatReportsData, ORDER_BY_COLUMNS);
  const flatReportsDataGroupedByKey = groupBy(sortedFlatReportsData, groupByKey);

  return Object.entries(flatReportsDataGroupedByKey).map(
    ([key, reportsDataGroupedByKey]) => ({
      headerName: key,
      colId: key,
      headerClass: 'columns-grouped-header',
      children: reportsDataGroupedByKey.map((reportData, index) => getColumnsPerGroup({
        reportData,
        setValue,
        headerClass: index === 0 ? 'columns-grouped-header': null,
        cellClass: index === 0 ? 'cell-border': null,
        isComparingMode,
        scenarioSettingsCollection,
        scenarioMembersMap,
        compareScenarioMembersMap,
        isFunctionalCurrencySelected,
      })),
    })
  );
};

export const getColumnDefinitions = ({
  reportsData,
  setValue,
  isComparingMode,
  scenarioSettingsCollection,
  scenarioMembersMap,
  compareScenarioMembersMap,
  isFunctionalCurrencySelected,
}) => [
  {
    headerName: 'Tax Expense Report',
    field: LABEL_COLUMN_ID,
    ...alignLeftCellStyle,
    minWidth: 250,
    width: 350,
    cellClassRules: {
      'cell-summary': isSummaryCell,
    },
    cellRenderer: 'agGroupCellRenderer',
    pinned: 'left',
  },
  isComparingMode
    ? getBaseCompareVarianceTotalColumn()
    : getTotalColumn(),
  ...getColumnDefinitionsGroupedBy({
    reportsData,
    setValue,
    isComparingMode,
    scenarioSettingsCollection,
    scenarioMembersMap,
    compareScenarioMembersMap,
    groupByKey: 'COMPANY_CODE',
    isFunctionalCurrencySelected,
  }),
];
